import { initAppConfiguration } from '@app/utils/configuration';
import DOMRender from '@app/utils/DOMRender';
import addDialogHandler from '@features/dialog/events';
import { addBtnDropdownToggleHandler } from '@features/dropdown/events';
import { addPasswordUnlockHandler } from '@features/form/button.events';
import { addModalEventHandlers } from '@features/modal/events';
import { addGdprRemoveUserDataHandler } from '@features/myprofile/events';
import { initializePhotoswipe } from '@features/photoswipe/events';
import { addBasketAddProductHandler } from '@features/purchasing/events';
import { addRegistrationFormHandler } from '@features/registration/events';
import { initializeSplideJS } from '@features/splidejs/events';
import { addTabsHandlers } from '@features/tabs/events';
import { addAccordionToggleHandler } from '@store/features/accordion/events';
import { addSearchWidgetOpenHandler } from '@store/features/search/events';

const query = (selector: string) => document.querySelector(selector) as HTMLElement;
// const queryAll = (selector: string) => document.querySelectorAll(selector);

async function loadTranslations() {
  const I18n = await import('i18next');
  const HttpApi = await import('i18next-http-backend');
  const I18React = await import('react-i18next');

  return I18n.default
    .use(HttpApi.default)
    .use(I18React.initReactI18next) // passes i18n down to react-i18next
    .init({
      backend: {
        loadPath: '/{lng}/localization.json',
      },
      lng: document.querySelector('html')!.lang ?? 'sk',
      fallbackLng: 'sk',
      interpolation: {
        escapeValue: false,
        prefix: '{',
        suffix: '}',
      },
    });
}

async function InitializeAsync() {
  // Configuration
  initAppConfiguration();

  // I18n
  await loadTranslations();

  // Events
  addDialogHandler();
  addGdprRemoveUserDataHandler();
  addSearchWidgetOpenHandler();
  addRegistrationFormHandler();
  addAccordionToggleHandler();
  addBtnDropdownToggleHandler();
  addTabsHandlers();
  addModalEventHandlers();
  addBasketAddProductHandler();
  addPasswordUnlockHandler();

  // 3rd party widgets
  initializePhotoswipe();
  initializeSplideJS();

  // Components
  const alertsElement = query('[data-react="alerts-list"]');
  if (alertsElement) {
    const Import = await import('@features/alerts');
    DOMRender(alertsElement, Import.default, { withQueryClient: false });
  }

  const navigationMobileElement = query('[data-react="navigation-mobile"]');
  if (navigationMobileElement) {
    const Import = await import('@features/navigation/SidebarMobile');
    DOMRender(navigationMobileElement, Import.default, { withQueryClient: true });
  }

  const searchFormElement = query('[data-react="search-form"]');
  if (searchFormElement) {
    const Import = await import('@features/search/SearchForm');
    DOMRender(searchFormElement, Import.default, { withQueryClient: true });
  }
  const searchFormMobileElement = query('[data-react="search-form-mobile"]');
  if (searchFormMobileElement) {
    const Import = await import('@features/search/SearchForm');
    DOMRender(searchFormMobileElement, Import.default, { withQueryClient: true });
  }

  const catalogFilterElement = query('[data-react="catalog-filter"]');
  if (catalogFilterElement) {
    const Import = await import('@features/catalog/filter');
    DOMRender(catalogFilterElement, Import.default, { withQueryClient: true });
  }

  // Pages
  const myBasketElement = query('[data-react="my-basket"]');
  if (myBasketElement) {
    const Import = await import('@features/purchasing/my-basket');
    DOMRender(myBasketElement, Import.default, { withQueryClient: true });
  }

  const checkoutElement = query('[data-react="basket-checkout"]');
  if (checkoutElement) {
    const Import = await import('@features/purchasing/checkout');
    DOMRender(checkoutElement, Import.default, { withQueryClient: true });
  }
}

InitializeAsync().then(() => console.debug('Scripts initialized'));
